/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { forwardRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { walletService } from '../../services/api';
import { Button } from '../console/ui';
import { formatTimeLeft, money } from './billingUtils';
import { openRazorpayCheckout, useInvalidateWallet } from './useBilling';

const AMOUNTS = [1000, 5000, 10000, 25000];
const MIN_TOPUP = 100;

const amtCls = (on: boolean) =>
  `min-h-[48px] px-4 rounded-[10px] border text-base font-semibold transition-colors ${
    on ? 'border-ps-accent bg-ps-accent-soft text-ps-accent' : 'border-ps-strong bg-ps-chrome text-ps-text hover:border-ps-muted'
  }`;

/** Add funds via Razorpay (UPI, cards, net banking). Amounts are in INR. */
const TopUpCard = forwardRef<
  HTMLInputElement,
  { balance: number; exchangeRate: number; hourlyRunRate: number }
>(({ balance, exchangeRate, hourlyRunRate }, amountRef) => {
  const invalidate = useInvalidateWallet();
  const [amount, setAmount] = useState(5000);

  const topUpMutation = useMutation({
    mutationFn: (amt: number) => walletService.topUp(amt, 'card'),
    onSuccess: async (response) => {
      await openRazorpayCheckout(
        response.data,
        async (paymentResponse) => {
          try {
            await walletService.verifyPayment(
              paymentResponse.razorpay_order_id,
              paymentResponse.razorpay_payment_id,
              paymentResponse.razorpay_signature,
            );
            toast.success('Payment successful!');
            invalidate();
          } catch {
            toast.error('Payment verification failed');
          }
        },
        { prefill: response.data.prefill },
      );
    },
  });

  const hours = hourlyRunRate > 0 ? amount / hourlyRunRate : 0;

  return (
    <section className="ps-card p-[22px] flex flex-col gap-4 flex-[3_1_480px] min-w-0" aria-labelledby="top">
      <div className="flex justify-between items-baseline gap-3 flex-wrap">
        <h2 id="top" className="m-0 text-lg font-medium text-ps-text">
          Add funds
        </h2>
        <span className="text-ps-muted text-sm">Balance {money(balance, 'INR', exchangeRate)}</span>
      </div>

      <p className="m-0 text-sm text-ps-muted">UPI, cards and net banking.</p>

      <div
        className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(min(100%,120px),1fr))]"
        role="group"
        aria-label="Amount"
      >
        {AMOUNTS.map((a) => (
          <button key={a} type="button" aria-pressed={amount === a} className={amtCls(amount === a)} onClick={() => setAmount(a)}>
            ₹{a.toLocaleString('en-IN')}
          </button>
        ))}
      </div>
      <label className="flex flex-col gap-1.5 text-sm">
        <span className="text-ps-muted text-[13px]">Or enter an amount</span>
        <input
          ref={amountRef}
          type="number"
          className="ps-input"
          min={MIN_TOPUP}
          step={100}
          placeholder="₹ Custom amount"
          value={amount || ''}
          onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
        />
        {amount > 0 && amount < MIN_TOPUP && <span className="text-xs text-ps-err">Minimum top-up is ₹{MIN_TOPUP}.</span>}
      </label>
      {hours > 0 && (
        <div className="flex justify-between gap-3 text-sm pt-1.5 border-t border-ps-border">
          <span className="text-ps-muted">At your current run rate ({money(hourlyRunRate, 'INR', exchangeRate)}/hr) that covers about</span>
          <span className="text-ps-text whitespace-nowrap">{formatTimeLeft(hours)}</span>
        </div>
      )}

      <Button
        className="self-start"
        onClick={() => topUpMutation.mutate(amount)}
        disabled={amount < MIN_TOPUP || topUpMutation.isPending}
      >
        {topUpMutation.isPending ? 'Processing…' : `Pay ₹${amount.toLocaleString('en-IN')}`}
      </Button>
      <p className="m-0 text-xs text-ps-subtle">
        Payment opens the secure payment provider (Razorpay). Podstack never shows card numbers here.
      </p>
    </section>
  );
});

TopUpCard.displayName = 'TopUpCard';

export default TopUpCard;
